import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { PalantirButtonComponent } from '@flux/design-system';
import type {
  ChatAction,
  ChatActionStatus,
  ChatComponent,
  ChatComponentActionEvent,
} from '../../models/chat.model';
import { FluxDetailComponent } from '../chat-components/flux-detail/flux-detail.component';
import { FluxListComponent } from '../chat-components/flux-list/flux-list.component';

/** Rótulos do botão por estado runtime da ação `tool_call`. */
const ACTION_STATUS_LABELS: Record<ChatActionStatus, string> = {
  running: 'Executando…',
  queued: 'Aguardando aprovação',
  done: 'Concluído',
};

/**
 * Ponto de integração que mapeia o `componentType` de um componente da Iara para
 * o renderer concreto, e renderiza a barra de ações declaradas pelo backend.
 * Tipos não implementados caem no `@default` (fallbackText). As ações são
 * `follow_up` (re-prompt), `navigate` (abre URL) e `tool_call` (executa tool
 * server-side; o `chat-page` decide o efeito). O estado runtime de cada
 * `tool_call` chega por `actionStates` para refletir executando/aprovação/feito.
 */
@Component({
  selector: 'app-chat-component-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FluxListComponent, FluxDetailComponent, PalantirButtonComponent],
  template: `
    @switch (component().componentType) {
      @case ('flux.list@1') {
        <app-flux-list [component]="component()" />
      }
      @case ('flux.detail@1') {
        <app-flux-detail [component]="component()" />
      }
      @default {
        <p class="chat-component-host__fallback" data-testid="chat-component-fallback">
          {{ component().fallbackText }}
        </p>
      }
    }

    @if (component().actions.length) {
      <div class="chat-component-host__actions" data-testid="chat-component-actions">
        @for (action of component().actions; track action.id) {
          <palantir-button
            theme="outline"
            [variant]="action.destructive ? 'negative' : 'neutral'"
            size="xs"
            [disabled]="isDisabled(action)"
            [attr.data-testid]="'chat-component-action-' + action.id"
            (click)="actionTriggered.emit({ action, component: component() })"
          >
            {{ labelFor(action) }}
          </palantir-button>
        }
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .chat-component-host__fallback {
      margin: 12px 0 0;
      font-size: 14.5px;
      line-height: 1.6;
      white-space: pre-wrap;
      color: var(--palantir-color-neutrals-text-default);
    }

    .chat-component-host__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 8px;
    }
  `,
})
export class ChatComponentHostComponent {
  public readonly component = input.required<ChatComponent>();

  /**
   * Estado runtime das ações `tool_call`, indexado por `action.id`. `undefined`
   * (ou ausência da entrada) = ação ainda não disparada.
   */
  public readonly actionStates = input<Record<string, ChatActionStatus> | undefined>(undefined);

  /** Ação disparada pelo usuário (o `chat-page` decide o efeito). */
  public readonly actionTriggered = output<ChatComponentActionEvent>();

  protected labelFor(action: ChatAction): string {
    const status = this.actionStates()?.[action.id];
    return status ? ACTION_STATUS_LABELS[status] : action.label;
  }

  /** Desabilita ações já marcadas pelo backend ou em execução/aprovação/concluídas. */
  protected isDisabled(action: ChatAction): boolean {
    return (action.disabled ?? false) || this.actionStates()?.[action.id] !== undefined;
  }
}
